import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Paper, Typography, Chip, Button, TextField } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import { fetchTenants, postTenantAction } from '../../data/apiClient';
import type { TenantSummary } from '../../shared/types';

const TenantsScreen: React.FC = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const load = (): void => {
    fetchTenants()
      .then(setTenants)
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    load();
  }, []);

  const canCreate = name.trim().length > 0 && code.trim().length > 0 && adminEmail.trim().length > 0;

  const handleCreate = (): void => {
    setError(null);
    postTenantAction({ type: 'CREATE_TENANT', name: name.trim(), code: code.trim(), adminEmail: adminEmail.trim() })
      .then(() => {
        setName('');
        setCode('');
        setAdminEmail('');
        load();
      })
      .catch((e: Error) => setError(t('tenants.actionFailed', { message: e.message })));
  };

  const handleToggleActive = (tenant: TenantSummary): void => {
    setError(null);
    postTenantAction({
      type: tenant.isActive ? 'DEACTIVATE_TENANT' : 'REACTIVATE_TENANT',
      tenantId: tenant.tenantId,
    })
      .then(load)
      .catch((e: Error) => setError(t('tenants.actionFailed', { message: e.message })));
  };

  const handleRename = (tenantId: string): void => {
    if (!editingName.trim()) return;
    setError(null);
    postTenantAction({ type: 'RENAME_TENANT', tenantId, name: editingName.trim() })
      .then(() => {
        setEditingId(null);
        load();
      })
      .catch((e: Error) => setError(t('tenants.actionFailed', { message: e.message })));
  };

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.1em', px: 0.5, mb: 1 }}>
        {t('tenants.title')}
      </Typography>

      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #F5F5F5' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>{t('tenants.createTitle')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('tenants.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            size="small"
            placeholder={t('tenants.codePlaceholder')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <TextField
            size="small"
            placeholder={t('tenants.adminEmailPlaceholder')}
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
          />
          <Button variant="contained" startIcon={<AddRounded />} disabled={!canCreate} onClick={handleCreate}>
            {t('tenants.create')}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Typography sx={{ color: 'error.main', textAlign: 'center', py: 1, fontSize: '0.85rem' }}>{error}</Typography>
      )}

      {tenants.length === 0 && (
        <Typography sx={{ px: 0.5, color: '#9E9E9E', fontSize: '0.85rem' }}>{t('tenants.noTenants')}</Typography>
      )}

      <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #F5F5F5' }}>
        {tenants.map((tenant) => (
          <Box key={tenant.tenantId} sx={{ px: 1.75, py: 1.5, borderBottom: '1px solid #F9F9F9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {editingId === tenant.tenantId ? (
                <>
                  <TextField
                    size="small"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button size="small" onClick={() => handleRename(tenant.tenantId)}>
                    {t('admin.save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    {t('admin.cancel')}
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', flex: 1 }}>
                    {tenant.name}{' '}
                    <Typography component="span" sx={{ color: '#9E9E9E', fontSize: '0.75rem' }}>
                      ({tenant.code})
                    </Typography>
                  </Typography>
                  <Chip
                    size="small"
                    label={tenant.isActive ? t('tenants.active') : t('tenants.inactive')}
                    sx={{
                      backgroundColor: tenant.isActive ? '#E8F5E9' : '#FFCDD2',
                      color: tenant.isActive ? '#2E7D32' : 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#9E9E9E', mb: 1 }}>
              {t('tenants.staffCount', { count: tenant.staffCount })} · {t('tenants.adminCount', { count: tenant.adminCount })}
            </Typography>
            {editingId !== tenant.tenantId && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingId(tenant.tenantId);
                    setEditingName(tenant.name);
                  }}
                >
                  {t('tenants.rename')}
                </Button>
                <Button size="small" color={tenant.isActive ? 'error' : 'primary'} onClick={() => handleToggleActive(tenant)}>
                  {tenant.isActive ? t('tenants.deactivate') : t('tenants.reactivate')}
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default TenantsScreen;
